import React from 'react';
import { AlertCircle } from 'lucide-react';
import { Button } from './ui/button';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error Boundary Component
 * 
 * Catches JavaScript errors anywhere in the child component tree
 * and displays a fallback UI instead of crashing the entire app.
 * 
 * Usage:
 * <ErrorBoundary>
 *   <YourComponent />
 * </ErrorBoundary>
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error to console (in production, send to error tracking service)
    console.error('Error Boundary caught an error:', error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <div 
          className="min-h-screen flex items-center justify-center p-6"
          style={{ backgroundColor: 'var(--background)' }}
        >
          <div 
            className="max-w-md w-full rounded-lg p-8 text-center"
            style={{ 
              backgroundColor: 'var(--card)',
              boxShadow: 'var(--elevation-md)',
              border: '1px solid var(--border)'
            }}
          >
            <div 
              className="w-16 h-16 rounded-full mx-auto flex items-center justify-center mb-4"
              style={{ backgroundColor: 'var(--infrared-coral-10)' }}
            >
              <AlertCircle 
                className="w-8 h-8" 
                style={{ color: 'var(--infrared-coral)' }}
              />
            </div>
            
            <h3 
              className="mb-2"
              style={{ 
                fontSize: 'var(--text-xl)',
                fontWeight: 'var(--font-weight-semibold)',
                color: 'var(--foreground)'
              }}
            >
              Something went wrong
            </h3>
            
            <p 
              className="mb-6"
              style={{ 
                fontSize: 'var(--text-base)',
                color: 'var(--muted-foreground)',
                lineHeight: '1.5'
              }}
            >
              We've encountered an unexpected error. Please try refreshing the page.
            </p>

            {/* Show error details in development */}
            {import.meta.env.DEV && this.state.error && (
              <details 
                className="mb-6 text-left p-4 rounded"
                style={{ 
                  backgroundColor: 'var(--muted)',
                  fontSize: 'var(--text-sm)'
                }}
              >
                <summary 
                  className="cursor-pointer mb-2"
                  style={{ 
                    fontWeight: 'var(--font-weight-semibold)',
                    color: 'var(--foreground)'
                  }}
                >
                  Error Details (Dev Mode)
                </summary>
                <pre 
                  className="overflow-auto"
                  style={{ 
                    fontSize: '12px',
                    color: 'var(--muted-foreground)'
                  }}
                >
                  {this.state.error.toString()}
                  {this.state.error.stack && `\n\n${this.state.error.stack}`}
                </pre>
              </details>
            )}

            <div className="flex gap-3 justify-center">
              <Button
                onClick={this.handleReset}
                variant="outline"
              >
                Try Again
              </Button>
              <Button
                onClick={() => window.location.href = '/'}
                style={{
                  backgroundColor: 'var(--accent)',
                  color: 'var(--accent-foreground)'
                }}
              >
                Go Home
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Convenience hook for throwing errors that will be caught by ErrorBoundary
 * 
 * Usage:
 * const throwError = useErrorHandler();
 * 
 * try {
 *   // something that might fail
 * } catch (error) {
 *   throwError(error);
 * }
 */
export function useErrorHandler() {
  const [, setError] = React.useState();
  
  return React.useCallback((error: Error) => {
    setError(() => {
      throw error;
    });
  }, []);
}
